/**
 * サイン提案ガイド — v2
 *
 * 機能:
 *  - URL パラメータによる業種初期選択 (?type=xxx)
 *  - 業種タブ切替（下線インジケーター / roving tabindex / 矢印・Home・End キー対応）
 *  - モーダル表示/非表示（aria-labelledby 動的連携・フォーカストラップ・Esc 対応）
 *  - ピン編集モード: Shopify.designMode 時のみ自動 ON（本番は無効）
 *    ドラッグで位置変更 → 実移動時のみクリップボードに "XX, YY"（top, left 形式）をコピー
 *  - 重複 url_key 検知（designMode 時に console.warn）
 *  - GA4 イベント（trigger_source で pin/index/mobile_list を区別）
 *  - セクション再初期化・アンロード時のリスナー掃除
 */

(function () {
  'use strict';

  /* ============================================================
     GA4
     ============================================================ */
  const Analytics = {
    track(name, params) {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, params || {});
      }
    },
    tabSwitch(key) { this.track('sign_guide_tab_switch', { business_type: key }); },
    hotspotClick(key, cat, source) {
      this.track('sign_guide_hotspot_click', {
        business_type: key,
        category_name: cat,
        trigger_source: source,
      });
    },
    productClick(key, cat, title) { this.track('sign_guide_product_click', { business_type: key, category_name: cat, product_title: title }); },
    collectionClick(key, cat, title) { this.track('sign_guide_collection_click', { business_type: key, category_name: cat, collection_title: title }); },
    articleClick(key, title) { this.track('sign_guide_article_click', { business_type: key, article_title: title }); },
  };

  /* ============================================================
     SignGuide
     ============================================================ */
  class SignGuide {
    constructor(root) {
      this.root = root;
      this.tabs = root.querySelectorAll('.sign-guide__tab');
      this.panels = root.querySelectorAll('.sign-guide__panel');
      this.indicator = root.querySelector('.sign-guide__tabs-indicator');
      this.modal = root.querySelector('.sign-guide__modal');
      this.modalContent = this.modal ? this.modal.querySelector('[data-modal-content]') : null;
      this.toast = root.querySelector('[data-toast]');
      this.editHint = root.querySelector('[data-edit-hint]');

      this.currentBusinessKey = null;
      this.lastTrigger = null;
      this.isEditMode = false;
      this.toastTimer = null;
      this.focusTrapController = null;
      this.abortController = new AbortController();
      this.prefetchedBlocks = new Set();

      this.init();
    }

    init() {
      this.initTabs();
      this.initModalTriggers();
      this.initModalPrefetch();
      this.initModalClose();
      this.initArticleTracking();
      this.initUrlParam();
      this.initEditMode();
      this.checkDuplicateKeys();

      window.requestAnimationFrame(() => this.moveIndicatorToActive());
      window.addEventListener('resize', () => this.moveIndicatorToActive(), { signal: this.abortController.signal });
    }

    destroy() {
      this.abortController.abort();
      if (this.focusTrapController) this.focusTrapController.abort();
      if (this.toastTimer) clearTimeout(this.toastTimer);
      if (this.modal && !this.modal.hidden) {
        document.body.style.overflow = '';
      }
      if (this.root.__signGuideInstance === this) {
        delete this.root.__signGuideInstance;
      }
    }

    /* ---------- url_key 重複検知（M1） ---------- */
    checkDuplicateKeys() {
      if (!(window.Shopify && window.Shopify.designMode)) return;
      const keys = Array.from(this.tabs).map(t => t.dataset.businessKey);
      const seen = new Map();
      keys.forEach((k, i) => {
        if (!k) return;
        if (seen.has(k)) {
          // eslint-disable-next-line no-console
          console.warn(`[sign-guide] URLパラメータキー "${k}" が重複しています。各業種のキーは一意に設定してください。`);
        } else {
          seen.set(k, i);
        }
      });
    }

    /* ---------- URL param ---------- */
    initUrlParam() {
      const params = new URLSearchParams(window.location.search);
      const type = params.get('type');
      if (type) {
        const tab = Array.from(this.tabs).find(t => t.dataset.businessKey === type);
        if (tab) {
          this.activateTabByElement(tab, { silent: true });
          return;
        }
      }
      const active = this.root.querySelector('.sign-guide__tab--active') || this.tabs[0];
      if (active) this.currentBusinessKey = active.dataset.businessKey;
    }

    /* ---------- Tabs（M4: keyboard 強化） ---------- */
    initTabs() {
      const signal = this.abortController.signal;
      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => this.activateTabByElement(tab), { signal });
        tab.addEventListener('keydown', (e) => this.handleTabKeydown(e, tab), { signal });
      });
    }

    handleTabKeydown(e, tab) {
      const tabs = Array.from(this.tabs);
      const idx = tabs.indexOf(tab);
      let nextIdx = -1;
      switch (e.key) {
        case 'ArrowRight':
          nextIdx = (idx + 1) % tabs.length;
          break;
        case 'ArrowLeft':
          nextIdx = (idx - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIdx = 0;
          break;
        case 'End':
          nextIdx = tabs.length - 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      const next = tabs[nextIdx];
      next.focus();
      this.activateTabByElement(next);
    }

    activateTabByElement(tab, opts) {
      const target = tab.dataset.target;
      const key = tab.dataset.businessKey;
      opts = opts || {};

      this.tabs.forEach(t => {
        const on = t === tab;
        t.classList.toggle('sign-guide__tab--active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      });

      this.panels.forEach(p => {
        const on = p.dataset.industry === target;
        p.classList.toggle('sign-guide__panel--active', on);
        if (on) p.removeAttribute('hidden');
        else p.setAttribute('hidden', '');
      });

      this.moveIndicator(tab);

      if (key && key !== this.currentBusinessKey && !opts.silent) {
        Analytics.tabSwitch(key);
      }
      this.currentBusinessKey = key;
    }

    moveIndicator(tab) {
      if (!this.indicator || !tab) return;
      const parent = tab.parentElement;
      if (!parent) return;
      const pr = parent.getBoundingClientRect();
      const tr = tab.getBoundingClientRect();
      const scrollLeft = parent.scrollLeft;
      this.indicator.style.width = tr.width + 'px';
      this.indicator.style.transform = 'translateX(' + (tr.left - pr.left + scrollLeft) + 'px)';
    }

    moveIndicatorToActive() {
      const active = this.root.querySelector('.sign-guide__tab--active');
      if (active) this.moveIndicator(active);
    }

    /* ---------- Modal triggers（H2: source 区別） ---------- */
    initModalTriggers() {
      const signal = this.abortController.signal;
      this.root.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-modal-open]');
        if (!trigger) return;
        if (this.isEditMode && trigger.matches('.sign-guide__hotspot')) return;

        e.preventDefault();
        const blockId = trigger.dataset.blockId;
        const categoryName = trigger.dataset.categoryName || '';
        if (!blockId) return;

        const source = this.detectTriggerSource(trigger);
        this.openModal(blockId, trigger);
        Analytics.hotspotClick(this.currentBusinessKey, categoryName, source);
      }, { signal });
    }

    detectTriggerSource(el) {
      if (el.matches('.sign-guide__hotspot')) return 'pin';
      if (el.matches('.sign-guide__index-button')) return 'index';
      if (el.matches('.sign-guide__mobile-button')) return 'mobile_list';
      return 'unknown';
    }

    /* ---------- Modal hero image prefetch（hover/focus/touch で先読み） ---------- */
    initModalPrefetch() {
      // データセーバー有効時はネットワーク節約のため先読みしない
      const conn = navigator.connection;
      if (conn && conn.saveData) return;

      const signal = this.abortController.signal;
      const triggers = this.root.querySelectorAll('[data-modal-open]');
      triggers.forEach(trigger => {
        const handler = () => this.prefetchModalHero(trigger.dataset.blockId);
        trigger.addEventListener('mouseenter', handler, { signal });
        trigger.addEventListener('focus', handler, { signal });
        trigger.addEventListener('touchstart', handler, { signal, passive: true });
      });
    }

    prefetchModalHero(blockId) {
      if (!blockId || this.prefetchedBlocks.has(blockId)) return;
      this.prefetchedBlocks.add(blockId);

      const template = this.root.querySelector(`template[data-modal-content="${blockId}"]`);
      if (!template) return;

      const heroImg = template.content.querySelector('.sign-guide__modal-visual img');
      if (!heroImg) return;

      const src = heroImg.getAttribute('src');
      if (!src) return;

      const preloader = new Image();
      const sizes = heroImg.getAttribute('sizes');
      const srcset = heroImg.getAttribute('srcset');
      if (sizes) preloader.sizes = sizes;
      if (srcset) preloader.srcset = srcset;
      preloader.src = src;
    }

    initModalClose() {
      if (!this.modal) return;
      const signal = this.abortController.signal;
      this.modal.addEventListener('click', (e) => {
        if (e.target.closest('[data-modal-close]')) this.closeModal();
      }, { signal });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.modal.hidden) this.closeModal();
      }, { signal });
    }

    /* ---------- Modal open/close（M3: aria-labelledby 動的連携） ---------- */
    openModal(blockId, trigger) {
      if (!this.modal || !this.modalContent) return;
      const template = this.root.querySelector(`template[data-modal-content="${blockId}"]`);
      if (!template) return;

      this.modalContent.innerHTML = '';
      this.modalContent.appendChild(template.content.cloneNode(true));

      // aria-labelledby: dynamic title id
      const title = this.modalContent.querySelector('.sign-guide__modal-title');
      if (title) {
        const titleId = `sg-modal-title-${blockId}`;
        title.id = titleId;
        this.modal.setAttribute('aria-labelledby', titleId);
        this.modal.removeAttribute('aria-label');
      }

      // product link tracking
      const businessKey = this.currentBusinessKey;
      const categoryName = title ? title.textContent.trim() : '';
      this.modalContent.querySelectorAll('.sign-guide__product-link').forEach(link => {
        link.addEventListener('click', () => {
          Analytics.productClick(businessKey, categoryName, link.dataset.productTitle || '');
        });
      });

      // collection link tracking
      const collectionLink = this.modalContent.querySelector('[data-collection-link]');
      if (collectionLink) {
        collectionLink.addEventListener('click', () => {
          Analytics.collectionClick(
            businessKey,
            collectionLink.dataset.categoryName || categoryName,
            collectionLink.dataset.collectionTitle || ''
          );
        });
      }

      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.lastTrigger = trigger || null;
      this.setupFocusTrap();
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      this.modalContent.innerHTML = '';
      document.body.style.overflow = '';
      if (this.focusTrapController) {
        this.focusTrapController.abort();
        this.focusTrapController = null;
      }
      if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
        this.lastTrigger.focus();
        this.lastTrigger = null;
      }
    }

    setupFocusTrap() {
      if (!this.modal) return;
      const closeBtn = this.modal.querySelector('.sign-guide__modal-close');
      if (closeBtn) closeBtn.focus();

      if (this.focusTrapController) this.focusTrapController.abort();
      this.focusTrapController = new AbortController();

      this.modal.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;
        const focusable = this.modal.querySelectorAll('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }, { signal: this.focusTrapController.signal });
    }

    /* ---------- Article tracking ---------- */
    initArticleTracking() {
      const signal = this.abortController.signal;
      this.root.querySelectorAll('.sign-guide__article').forEach(a => {
        a.addEventListener('click', () => {
          Analytics.articleClick(this.currentBusinessKey, a.dataset.articleTitle || '');
        }, { signal });
      });
    }

    /* ---------- Edit mode（H1: designMode のみで判定。URL パラメータは廃止） ---------- */
    initEditMode() {
      const designMode = !!(window.Shopify && window.Shopify.designMode);
      this.setEditMode(designMode);

      if (designMode) {
        const signal = this.abortController.signal;
        document.addEventListener('shopify:block:select', (e) => {
          const blockId = e.detail && e.detail.blockId;
          this.highlightSelectedHotspot(blockId);
        }, { signal });
        document.addEventListener('shopify:block:deselect', () => {
          this.highlightSelectedHotspot(null);
        }, { signal });
      }

      this.initHotspotsForEdit();
      this.root.querySelectorAll('.sign-guide__hotspot').forEach(h => this.updateTooltip(h));
    }

    setEditMode(on) {
      this.isEditMode = !!on;
      this.root.classList.toggle('is-edit-mode', this.isEditMode);
      if (this.editHint) this.editHint.hidden = !this.isEditMode;
    }

    highlightSelectedHotspot(blockId) {
      this.root.querySelectorAll('.sign-guide__hotspot').forEach(h => {
        h.classList.toggle('is-selected', !!blockId && h.dataset.blockId === blockId);
      });
      if (!blockId) return;
      const pin = this.root.querySelector(`.sign-guide__hotspot[data-block-id="${blockId}"]`);
      if (pin) {
        const panelEl = pin.closest('.sign-guide__panel');
        if (panelEl && panelEl.dataset.industry) {
          const tab = this.root.querySelector(`.sign-guide__tab[data-target="${panelEl.dataset.industry}"]`);
          if (tab && !tab.classList.contains('sign-guide__tab--active')) {
            this.activateTabByElement(tab, { silent: true });
          }
        }
      }
    }

    initHotspotsForEdit() {
      let dragState = null;
      const DRAG_THRESHOLD = 3; // px; M2

      this.root.querySelectorAll('.sign-guide__hotspot').forEach(hotspot => {
        const signal = this.abortController.signal;

        hotspot.addEventListener('pointerdown', (e) => {
          if (!this.isEditMode) return;
          e.preventDefault();
          const plan = hotspot.closest('[data-plan]');
          if (!plan) return;
          const rect = plan.getBoundingClientRect();
          dragState = {
            hotspot,
            plan,
            rect,
            startX: e.clientX,
            startY: e.clientY,
            moved: false,
          };
          hotspot.classList.add('is-dragging');
          try { hotspot.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
        }, { signal });

        hotspot.addEventListener('pointermove', (e) => {
          if (!dragState || dragState.hotspot !== hotspot) return;
          if (!dragState.moved) {
            const dx = Math.abs(e.clientX - dragState.startX);
            const dy = Math.abs(e.clientY - dragState.startY);
            if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) dragState.moved = true;
          }
          const { rect } = dragState;
          let x = ((e.clientX - rect.left) / rect.width) * 100;
          let y = ((e.clientY - rect.top) / rect.height) * 100;
          x = Math.max(0, Math.min(100, x));
          y = Math.max(0, Math.min(100, y));
          hotspot.style.left = x + '%';
          hotspot.style.top = y + '%';
          this.updateTooltip(hotspot);
        }, { signal });

        const finish = (e) => {
          if (!dragState || dragState.hotspot !== hotspot) return;
          const movedEnough = dragState.moved;
          hotspot.classList.remove('is-dragging');
          try { hotspot.releasePointerCapture(e.pointerId); } catch (err) { /* noop */ }
          if (movedEnough) {
            const top = parseFloat(hotspot.style.top).toFixed(1);
            const left = parseFloat(hotspot.style.left).toFixed(1);
            this.copyToClipboard(`${top}, ${left}`);
            this.showToast(`コピー  Top ${top} / Left ${left}`);
          }
          dragState = null;
        };

        hotspot.addEventListener('pointerup', finish, { signal });
        hotspot.addEventListener('pointercancel', () => {
          if (dragState) {
            dragState.hotspot.classList.remove('is-dragging');
            dragState = null;
          }
        }, { signal });
      });
    }

    updateTooltip(hotspot) {
      const tip = hotspot.querySelector('[data-tooltip]');
      if (!tip) return;
      const top = parseFloat(hotspot.style.top) || 0;
      const left = parseFloat(hotspot.style.left) || 0;
      tip.textContent = `Top ${top.toFixed(1)} / Left ${left.toFixed(1)}`;
    }

    /* ---------- Clipboard & toast ---------- */
    copyToClipboard(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(() => this.fallbackCopy(text));
      } else {
        this.fallbackCopy(text);
      }
    }

    fallbackCopy(text) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* noop */ }
      document.body.removeChild(ta);
    }

    showToast(msg) {
      if (!this.toast) return;
      this.toast.textContent = msg || 'コピーしました';
      this.toast.classList.add('is-visible');
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => this.toast.classList.remove('is-visible'), 1800);
    }
  }

  /* ============================================================
     INIT & Shopify section lifecycle
     テーマエディタでセクションが再描画されると本 script がもう一度
     実行されうるため、document-level リスナーはグローバルガードで
     一度だけ登録する（M2: 多重登録防止）
     ============================================================ */
  function init() {
    document.querySelectorAll('.sign-guide').forEach(el => {
      if (!el.__signGuideInstance) {
        el.__signGuideInstance = new SignGuide(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  if (!window.__signGuideGlobalInit) {
    window.__signGuideGlobalInit = true;

    document.addEventListener('shopify:section:load', (e) => {
      const section = e.target.querySelector ? e.target.querySelector('.sign-guide') : null;
      if (section && !section.__signGuideInstance) {
        section.__signGuideInstance = new SignGuide(section);
      }
    });

    document.addEventListener('shopify:section:unload', (e) => {
      const section = e.target.querySelector ? e.target.querySelector('.sign-guide') : null;
      if (section && section.__signGuideInstance) {
        section.__signGuideInstance.destroy();
      }
    });
  }
})();
